/**
 * Production domain configuration
 * When running on nxt48.com, API and WebSocket use subdomains
 */
const PRODUCTION_CONFIG = {
  frontendDomain: 'nxt48.com',
  apiOrigin: 'https://api.nxt48.com',
  wssOrigin: 'https://wss.nxt48.com',
};

/**
 * Detect if we're running in production (on nxt48.com)
 */
function isProduction(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === PRODUCTION_CONFIG.frontendDomain || host === `www.${PRODUCTION_CONFIG.frontendDomain}`;
}

/**
 * Get API base URL
 * - Production: https://api.nxt48.com
 * - Development: window.location.origin (localhost or ngrok)
 */
function getApiUrl(): string {
  if (isProduction()) {
    return PRODUCTION_CONFIG.apiOrigin;
  }
  
  // Development: use same origin (works with localhost and ngrok)
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  
  // Fallback for env vars
  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl) return apiUrl.replace(/\/api\/?$/, '');
  
  return 'http://localhost:3000';
}

/**
 * Get WebSocket base URL
 * - Production: https://wss.nxt48.com
 * - Development: window.location.origin (localhost or ngrok)
 */
function getSocketUrl(): string {
  if (isProduction()) {
    return PRODUCTION_CONFIG.wssOrigin;
  }
  
  // Development: use same origin
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  
  // Fallback
  const socketUrl = import.meta.env.VITE_SOCKET_URL;
  if (socketUrl) return socketUrl;
  
  return 'http://localhost:3000';
}

// Cache the URLs at module load
const apiBaseUrl = getApiUrl();
const socketBaseUrl = getSocketUrl();

/**
 * NOTE: googleMapsApiKey and enableConsentBanner are now loaded from PublicConfigContext
 * at runtime. These are kept here as fallback for backwards compatibility.
 * 
 * To use the runtime config, use:
 * import { usePublicConfigContext } from '@/core/contexts/PublicConfigContext';
 * const { config } = usePublicConfigContext();
 * const googleMapsApiKey = config?.googleMapsApiKey || '';
 */
export const env = {
  /**
   * API URL with /api suffix
   * - Production: https://api.nxt48.com/api
   * - Development: http://localhost:3000/api or ngrok
   */
  get apiUrl() {
    return `${apiBaseUrl}/api`;
  },
  
  /**
   * WebSocket URL (base origin, socket.io adds its own path)
   * - Production: https://wss.nxt48.com
   * - Development: http://localhost:3000 or ngrok
   */
  get socketUrl() {
    return socketBaseUrl;
  },
  
  /**
   * Check if running in production
   */
  get isProduction() {
    return isProduction();
  },
  
  appName: import.meta.env.VITE_APP_NAME || import.meta.env.APP_NAME || 'NXT48',
  // Fallback values - should use PublicConfigContext in components
  googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
  enableConsentBanner: import.meta.env.VITE_ENABLE_CONSENT_BANNER === 'true',
} as const;
